import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { TallyPendingTransaction, vouchersApi, vchTypeApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';

type PendingSyncRow = TallyPendingTransaction;

type VoucherTypeChoice = { name?: string | null; parent_name?: string | null };
type TypeTotal = { name: string; count: number; amount: number };

const PAGE_SIZES = [25, 50, 100, 200];
const DEFAULT_PAGE_SIZE = 50;

const formatAmount = (value: unknown) =>
  Number(value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatDate = (value?: string | null) => {
  const raw = String(value || '').slice(0, 10);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : '—';
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const pendingReason = (row: PendingSyncRow) => {
  const supplied = String(row.pending_reason || '').trim();
  if (!supplied) return row.tally_master_synced_at ? 'Changed after sync' : 'Not synced yet';
  const normal = supplied.toLowerCase().replace(/[_-]+/g, ' ');
  if (normal.includes('not') && normal.includes('sync')) return 'Not synced yet';
  if (normal.includes('new') || normal.includes('never')) return 'Not synced yet';
  if (normal.includes('change') || normal.includes('edit') || normal.includes('update')) return 'Changed after sync';
  return supplied;
};

const isChangedSinceSync = (row: PendingSyncRow) => /changed|edit|update/i.test(pendingReason(row));

const normaliseTypeTotals = (raw: unknown): TypeTotal[] => {
  if (Array.isArray(raw)) {
    return raw.map((entry: any) => ({
      name: String(entry?.vch_type ?? entry?.voucher_type ?? entry?.type ?? entry?.name ?? 'Unspecified'),
      count: Number(entry?.count ?? entry?.voucher_count ?? entry?.total ?? 0),
      amount: Number(entry?.amount ?? entry?.total_amount ?? entry?.amount_total ?? 0),
    }));
  }
  if (raw && typeof raw === 'object') {
    return Object.entries(raw as Record<string, any>).map(([name, value]) => ({
      name,
      count: Number(value?.count ?? value?.voucher_count ?? value ?? 0),
      amount: Number(value?.amount ?? value?.total_amount ?? 0),
    }));
  }
  return [];
};

export default function PendingSyncTransactions() {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const dateFrom = searchParams.get('from') || '';
  const dateTo = searchParams.get('to') || '';
  const vchType = searchParams.get('type') || '';
  const search = searchParams.get('q') || '';
  const requestedPage = Math.max(1, Number(searchParams.get('page')) || 1);
  const requestedLimit = Number(searchParams.get('limit')) || DEFAULT_PAGE_SIZE;
  const limit = PAGE_SIZES.includes(requestedLimit) ? requestedLimit : DEFAULT_PAGE_SIZE;

  const [draftFrom, setDraftFrom] = useState(dateFrom);
  const [draftTo, setDraftTo] = useState(dateTo);
  const [draftType, setDraftType] = useState(vchType);
  const [draftSearch, setDraftSearch] = useState(search);
  const [voucherTypes, setVoucherTypes] = useState<string[]>([]);
  const [rows, setRows] = useState<PendingSyncRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(requestedPage);
  const [responseLimit, setResponseLimit] = useState(limit);
  const [totalAmount, setTotalAmount] = useState(0);
  const [newCount, setNewCount] = useState(0);
  const [updatedCount, setUpdatedCount] = useState(0);
  const [typeTotals, setTypeTotals] = useState<TypeTotal[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setDraftFrom(dateFrom); }, [dateFrom]);
  useEffect(() => { setDraftTo(dateTo); }, [dateTo]);
  useEffect(() => { setDraftType(vchType); }, [vchType]);
  useEffect(() => { setDraftSearch(search); }, [search]);

  useEffect(() => {
    let mounted = true;
    vchTypeApi.getAll()
      .then((res: any) => {
        if (!mounted || !res?.success) return;
        const names = Array.from(new Set((res.data || [])
          .map((type: VoucherTypeChoice) => String(type.parent_name || type.name || '').trim())
          .filter(Boolean))) as string[];
        setVoucherTypes(names.sort((a, b) => a.localeCompare(b)));
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  const writeParams = useCallback((next: {
    from?: string; to?: string; type?: string; q?: string; page?: number; limit?: number;
  }) => {
    const params: Record<string, string> = {};
    if (next.from) params.from = next.from;
    if (next.to) params.to = next.to;
    if (next.type) params.type = next.type;
    if (next.q) params.q = next.q;
    if ((next.page || 1) > 1) params.page = String(next.page);
    if ((next.limit || DEFAULT_PAGE_SIZE) !== DEFAULT_PAGE_SIZE) params.limit = String(next.limit);
    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await vouchersApi.getTallyPendingReport({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        vch_type: vchType || undefined,
        search: search || undefined,
        page: requestedPage,
        limit,
      });
      if (!response?.success) throw new Error(response?.message || 'Failed to load pending transactions');

      setRows((response.data || []) as PendingSyncRow[]);
      setTotal(Number(response.total || 0));
      setPage(Math.max(1, Number(response.page) || requestedPage));
      setResponseLimit(Math.max(1, Number(response.limit) || limit));
      setTotalAmount(Number(response.totals?.total_amount || 0));
      setNewCount(Number(response.totals?.new_count || 0));
      setUpdatedCount(Number(response.totals?.updated_count || 0));
      setTypeTotals(normaliseTypeTotals(response.type_totals));
    } catch (error: any) {
      setRows([]);
      setTotal(0);
      setTotalAmount(0);
      setNewCount(0);
      setUpdatedCount(0);
      setTypeTotals([]);
      showError('Unable to load pending sync transactions', error?.message || 'Please try again.');
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, vchType, search, requestedPage, limit, showError]);

  useEffect(() => { load(); }, [load]);

  const applyFilters = () => {
    if (draftFrom && draftTo && draftFrom > draftTo) {
      showError('Invalid date range', 'From date cannot be after To date.');
      return;
    }
    writeParams({
      from: draftFrom,
      to: draftTo,
      type: draftType,
      q: draftSearch.trim(),
      page: 1,
      limit,
    });
  };

  const clearFilters = () => {
    setDraftFrom('');
    setDraftTo('');
    setDraftType('');
    setDraftSearch('');
    writeParams({ page: 1, limit });
  };

  const filtersDirty = draftFrom !== dateFrom
    || draftTo !== dateTo
    || draftType !== vchType
    || draftSearch.trim() !== search;
  const hasFilters = !!(dateFrom || dateTo || vchType || search);
  const totalPages = Math.max(1, Math.ceil(total / responseLimit));
  const visiblePage = Math.min(page, totalPages);
  const rangeStart = total === 0 ? 0 : ((visiblePage - 1) * responseLimit) + 1;
  const rangeEnd = Math.min(visiblePage * responseLimit, total);

  const changePage = (nextPage: number) => {
    const safePage = Math.min(Math.max(1, nextPage), totalPages);
    if (safePage === visiblePage) return;
    writeParams({ from: dateFrom, to: dateTo, type: vchType, q: search, page: safePage, limit });
  };

  const changePageSize = (nextLimit: number) => {
    writeParams({ from: dateFrom, to: dateTo, type: vchType, q: search, page: 1, limit: nextLimit });
  };

  const typeSummary = useMemo(
    () => typeTotals.filter(item => item.name && (item.count > 0 || item.amount !== 0)),
    [typeTotals],
  );

  return (
    <div className="flex flex-col w-full fixed left-0 right-0 top-14 bottom-16 sm:static sm:h-full sm:top-auto sm:bottom-auto" style={{ overscrollBehavior: 'contain' }}>
      <div className="flex-none bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-1 min-w-0">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 -ml-1" title="Back">
            <ChevronLeft size={20} />
          </button>
          <Clock size={18} className="text-amber-600 shrink-0" />
          <div className="min-w-0">
            <h1 className="text-[17px] font-bold text-slate-800 truncate">Pending Sync Transactions</h1>
            <p className="text-[11px] text-slate-500 truncate">Vouchers waiting for the Tally sync API.</p>
          </div>
        </div>
        <button onClick={load} disabled={loading} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-50" title="Refresh">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <form onSubmit={event => { event.preventDefault(); applyFilters(); }} className="flex-none bg-slate-50 border-b border-slate-200 px-3 py-2.5">
        <div className="max-w-[1500px] mx-auto flex flex-wrap gap-2 items-end">
          <label className="relative flex-1 min-w-[210px]">
            <span className="sr-only">Search</span>
            <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={draftSearch} onChange={event => setDraftSearch(event.target.value)}
              placeholder="Search voucher no., party, or voucher type..."
              className="w-full pl-8 pr-3 py-2 rounded border border-slate-300 bg-white text-[13px] outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
          </label>
          <label className="min-w-[160px]">
            <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Voucher Type</span>
            <select value={draftType} onChange={event => setDraftType(event.target.value)} className="w-full rounded border border-slate-300 bg-white px-2.5 py-2 text-[13px] text-slate-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500">
              <option value="">All types</option>
              {voucherTypes.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>
          <label className="min-w-[138px]">
            <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">From</span>
            <span className="relative block">
              <Calendar size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input type="date" value={draftFrom} onChange={event => setDraftFrom(event.target.value)} className="w-full rounded border border-slate-300 bg-white pl-8 pr-2 py-2 text-[13px] text-slate-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
            </span>
          </label>
          <label className="min-w-[138px]">
            <span className="block text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">To</span>
            <span className="relative block">
              <Calendar size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input type="date" value={draftTo} onChange={event => setDraftTo(event.target.value)} className="w-full rounded border border-slate-300 bg-white pl-8 pr-2 py-2 text-[13px] text-slate-700 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500" />
            </span>
          </label>
          <button type="submit" disabled={!filtersDirty || loading} className="rounded bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed px-3 py-2 text-[13px] font-medium text-white">
            Apply
          </button>
          {hasFilters && (
            <button type="button" onClick={clearFilters} disabled={loading} className="flex items-center gap-1 rounded border border-slate-300 bg-white hover:bg-slate-100 disabled:opacity-50 px-3 py-2 text-[13px] text-slate-600">
              <X size={14} /> Clear
            </button>
          )}
        </div>
      </form>

      <div className="flex-1 min-h-0 overflow-auto bg-slate-100 p-3">
        <div className="max-w-[1500px] mx-auto space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="text-[11px] font-bold uppercase tracking-wide text-amber-700">Transactions pending</div>
              <div className="mt-1 text-2xl font-bold tabular-nums text-amber-900">{total.toLocaleString('en-IN')}</div>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white px-4 py-3">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Pending transaction amount</div>
              <div className="mt-1 text-2xl font-bold tabular-nums text-slate-900">{formatAmount(totalAmount)}</div>
            </div>
            <div className="rounded-lg border border-amber-200 bg-white px-4 py-3">
              <div className="text-[11px] font-bold uppercase tracking-wide text-amber-700">New - not synced</div>
              <div className="mt-1 text-2xl font-bold tabular-nums text-amber-900">{newCount.toLocaleString('en-IN')}</div>
            </div>
            <div className="rounded-lg border border-blue-200 bg-white px-4 py-3">
              <div className="text-[11px] font-bold uppercase tracking-wide text-blue-700">Changed after sync</div>
              <div className="mt-1 text-2xl font-bold tabular-nums text-blue-900">{updatedCount.toLocaleString('en-IN')}</div>
            </div>
          </div>

          {typeSummary.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white px-3 py-2.5">
              <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Pending by voucher type</div>
              <div className="flex flex-wrap gap-2">
                {typeSummary.map(item => (
                  <div key={item.name} className="rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[12px] text-slate-700">
                    <span className="font-semibold">{item.name}</span>
                    <span className="ml-2 tabular-nums text-blue-700">{item.count}</span>
                    <span className="ml-2 tabular-nums text-slate-500">{formatAmount(item.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-lg border border-slate-300 bg-white overflow-hidden">
            {loading ? (
              <div className="py-16 text-center text-sm text-slate-400">
                <RefreshCw size={20} className="animate-spin mx-auto mb-2 text-blue-400" />
                Loading pending transactions...
              </div>
            ) : rows.length === 0 ? (
              <div className="py-16 text-center text-sm text-slate-400">
                <Clock size={24} className="mx-auto mb-2 text-slate-300" />
                No transactions are waiting for sync.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px] text-left border-collapse">
                  <thead>
                    <tr className="bg-slate-200 text-[11px] uppercase tracking-wide text-slate-600">
                      <th className="border-b border-slate-300 px-3 py-2 font-bold w-12">#</th>
                      <th className="border-b border-slate-300 px-3 py-2 font-bold whitespace-nowrap">Date</th>
                      <th className="border-b border-slate-300 px-3 py-2 font-bold">Voucher</th>
                      <th className="border-b border-slate-300 px-3 py-2 font-bold">Type</th>
                      <th className="border-b border-slate-300 px-3 py-2 font-bold">Party</th>
                      <th className="border-b border-slate-300 px-3 py-2 font-bold">Pending because</th>
                      <th className="border-b border-slate-300 px-3 py-2 font-bold text-right">Amount</th>
                      <th className="border-b border-slate-300 px-3 py-2 font-bold whitespace-nowrap">Last changed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const reason = pendingReason(row);
                      const changed = isChangedSinceSync(row);
                      const typeName = row.vch_subtype && row.vch_subtype !== row.vch_type
                        ? `${row.vch_type || 'Voucher'} / ${row.vch_subtype}`
                        : (row.vch_type || row.vch_subtype || '—');
                      return (
                        <tr key={row.id} className="border-b border-slate-100 last:border-b-0 hover:bg-blue-50/60">
                          <td className="px-3 py-2 text-[13px] tabular-nums text-slate-400">{rangeStart + index}</td>
                          <td className="px-3 py-2 text-[13px] text-slate-700 whitespace-nowrap">{formatDate(row.vch_date)}</td>
                          <td className="px-3 py-2 text-[13px] whitespace-nowrap">
                            <button onClick={() => navigate(`/billing/vouchers/edit/${row.id}`)} className="font-semibold text-blue-700 hover:text-blue-900 hover:underline" title="Open voucher">
                              {row.vch_no || '—'}
                            </button>
                          </td>
                          <td className="px-3 py-2 text-[13px] text-slate-700">{typeName}</td>
                          <td className="px-3 py-2 text-[13px] text-slate-800 max-w-[280px]">
                            <div className="truncate font-medium" title={row.party_name || ''}>{row.party_name || '—'}</div>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap ${changed ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-800'}`}>
                              {reason}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-[13px] text-right font-semibold tabular-nums text-slate-800 whitespace-nowrap">{formatAmount(row.amount)}</td>
                          <td className="px-3 py-2 text-[12px] text-slate-500 whitespace-nowrap">{formatDateTime(row.updated_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {!loading && total > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-3 py-2.5 text-[12px] text-slate-600">
                <span>Showing <strong className="text-slate-800">{rangeStart}-{rangeEnd}</strong> of <strong className="text-slate-800">{total.toLocaleString('en-IN')}</strong></span>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-1.5">
                    <span>Rows</span>
                    <select value={limit} onChange={event => changePageSize(Number(event.target.value))} className="rounded border border-slate-300 bg-white px-1.5 py-1 text-[12px] text-slate-700">
                      {PAGE_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
                    </select>
                  </label>
                  <div className="flex items-center gap-1">
                    <button onClick={() => changePage(1)} disabled={visiblePage <= 1} className="rounded p-1 text-slate-600 hover:bg-slate-200 disabled:opacity-35" title="First page"><ChevronsLeft size={16} /></button>
                    <button onClick={() => changePage(visiblePage - 1)} disabled={visiblePage <= 1} className="rounded p-1 text-slate-600 hover:bg-slate-200 disabled:opacity-35" title="Previous page"><ChevronLeft size={16} /></button>
                    <span className="min-w-[72px] text-center tabular-nums">Page {visiblePage} / {totalPages}</span>
                    <button onClick={() => changePage(visiblePage + 1)} disabled={visiblePage >= totalPages} className="rounded p-1 text-slate-600 hover:bg-slate-200 disabled:opacity-35" title="Next page"><ChevronRight size={16} /></button>
                    <button onClick={() => changePage(totalPages)} disabled={visiblePage >= totalPages} className="rounded p-1 text-slate-600 hover:bg-slate-200 disabled:opacity-35" title="Last page"><ChevronsRight size={16} /></button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
